import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link as RouterLink } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Skeleton,
  AppBar,
  Toolbar,
  IconButton,
  Fab,
  Snackbar,
  Alert,
  Chip,
  Rating,
  Link,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slide,
} from '@mui/material';
import {
  Landscape,
  Phone,
  Email,
  LocationOn,
  Star,
  Grass,
  Build,
  Send,
  WhatsApp,
  Schedule,
  CheckCircle,
  ExpandMore,
  WaterDrop,
  LocalFlorist,
  ArrowBackIos,
  ArrowForwardIos,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Map
} from '@mui/icons-material';
import { useForm } from '@formspree/react';
import LawnCareVancouver from './pages/LawnCareVancouver';
import IrrigationSystems from './pages/IrrigationSystems';
import GardenDesign from './pages/GardenDesign';
import Hardscaping from './pages/Hardscaping';
import LandscapeMaintenance from './pages/LandscapeMaintenance';
import GetQuote from './pages/GetQuote';

const theme = createTheme({
  palette: {
    primary: { main: '#1b5e20' },
    secondary: { main: '#4caf50' },
    success: { main: '#2e7d32' },
    background: { default: '#f1f8e9', paper: '#ffffff' }
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
    h1: { fontWeight: 700, fontSize: { xs: '2.5rem', md: '4rem' } },
    h2: { fontWeight: 600, fontSize: { xs: '2rem', md: '3rem' } },
    h3: { fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } },
    h4: { fontWeight: 600 },
    body1: { lineHeight: 1.8 }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }
      }
    }
  }
});

const glassStyle = {
  background: 'rgba(255, 255, 255, 0.25)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
};

const services = [
  { icon: <Landscape />, title: 'Landscape Design', desc: 'Custom outdoor designs that reflect your style and enhance your property value', features: ['3D Design Concepts', 'Plant Selection', 'Hardscape Planning'] },
  { icon: <Grass />, title: 'Lawn Care & Maintenance', desc: 'Keep your lawn healthy and beautiful year-round with our comprehensive care', features: ['Weekly Mowing', 'Fertilization', 'Weed Control', 'Aeration'], link: '/lawn-care-vancouver' },
  { icon: <LocalFlorist />, title: 'Garden Design & Planting', desc: 'Beautiful flower beds and garden spaces tailored to North Vancouver climate', features: ['Seasonal Planting', 'Perennial Gardens', 'Native Plants'], link: '/garden-design' },
  { icon: <Build />, title: 'Hardscaping & Patios', desc: 'Durable and attractive stone work, patios, walkways and retaining walls', features: ['Natural Stone', 'Interlocking Pavers', 'Retaining Walls'], link: '/hardscaping' },
  { icon: <WaterDrop />, title: 'Irrigation Systems', desc: 'Efficient watering solutions to keep your landscape thriving', features: ['Drip Systems', 'Sprinkler Installation', 'Smart Controllers'], link: '/irrigation-systems' },
  { icon: <Build />, title: 'Landscape Maintenance', desc: 'Regular upkeep to ensure your outdoor space stays pristine', features: ['Pruning & Trimming', 'Mulching', 'Seasonal Cleanup'], link: '/landscape-maintenance' }
];

const testimonials = [
  { name: 'Sarah M.', rating: 5, text: 'Green Mountain Landscaping transformed our backyard into a stunning outdoor retreat. Their attention to detail and professionalism exceeded our expectations. Highly recommended!', location: 'North Vancouver' },
  { name: 'Mike R.', rating: 5, text: 'Exceptional service from consultation to completion. The team was punctual, respectful, and delivered exactly what we envisioned for our front yard landscape design.', location: 'West Vancouver' },
  { name: 'Jennifer L.', rating: 5, text: 'Outstanding craftsmanship and customer service. Our new patio and garden installation looks absolutely amazing. Worth every penny!', location: 'Burnaby' },
  { name: 'David K.', rating: 5, text: 'Professional, reliable, and creative. Green Mountain helped us design a beautiful sustainable garden that perfectly suits our North Vancouver property.', location: 'North Vancouver' }
];

const faqs = [
  { q: 'Do you provide free estimates?', a: 'Yes! We offer free, detailed estimates for all landscaping projects in North Vancouver and surrounding Metro Vancouver areas. Contact us to schedule your consultation.' },
  { q: 'What areas do you serve?', a: 'We proudly serve North Vancouver, West Vancouver, Burnaby, Richmond, Vancouver, and surrounding Metro Vancouver communities.' },
  { q: 'Are you licensed and insured?', a: 'Absolutely. Green Mountain Landscaping is fully licensed and insured for your complete peace of mind and protection.' },
  { q: 'What services do you offer?', a: 'We provide comprehensive landscaping services including design, installation, maintenance, hardscaping, irrigation, lawn care, and seasonal cleanup.' },
  { q: 'How long have you been in business?', a: 'Green Mountain Landscaping has been serving the North Vancouver community since 2019, building a reputation for quality and reliability.' },
  { q: 'Do you offer maintenance services?', a: 'Yes! We provide ongoing landscape maintenance including lawn care, pruning, fertilization, seasonal cleanup, and irrigation system maintenance.' }
];

const portfolioImages = [
  { src: '/images/348s.jpg', alt: 'Professional landscape design North Vancouver' },
  { src: '/images/348s (1).jpg', alt: 'Garden maintenance and lawn care services' },
  { src: '/images/348s (2).jpg', alt: 'Custom hardscaping and patio installation' },
  { src: '/images/o.jpg', alt: 'Beautiful garden design and planting' },
  { src: '/images/o (1).jpg', alt: 'Landscape transformation project' },
  { src: '/images/o (2).jpg', alt: 'Outdoor living space design' },
  { src: '/images/4.webp', alt: 'Professional landscaping maintenance' },
  { src: '/images/7.webp', alt: 'Stone work and retaining walls' },
  { src: '/images/10.webp', alt: 'Complete landscape renovation' },
  { src: '/images/۱.jpg', alt: 'Green Mountain Landscaping portfolio' }
];

const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = React.useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <Typography ref={ref} variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
      {count}{suffix}
    </Typography>
  );
};

const HomePage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '', service: '' });
  // Removed unused loading state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % portfolioImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollButtons(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToBottom = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % portfolioImages.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + portfolioImages.length) % portfolioImages.length);

  const [state, handleSubmit] = useForm("xzzglnqq");
  
  React.useEffect(() => {
    if (state.succeeded) {
      setSnackbar({ open: true, message: 'Message sent successfully!', severity: 'success' });
      setFormData({ name: '', email: '', phone: '', message: '', service: '' });
    }
    if (state.errors && state.errors.length > 0) {
      setSnackbar({ open: true, message: 'Failed to send message. Please try again.', severity: 'error' });
    }
  }, [state.succeeded, state.errors]);

  return (
    <>
      <Helmet>
        <title>Green Mountain Landscaping | Premier Landscape Design West Vancouver BC | Hardscaping & Lawn Care</title>
        <meta name="description" content="Green Mountain Landscaping - Premier landscape design, lawn care, hardscaping & garden maintenance in West Vancouver, North Vancouver & Metro Vancouver BC. Licensed, insured, free quotes. Call (604) 202-0053" />
        <link rel="canonical" href="https://greenmountainlandscaping.ca" />
      </Helmet>
      
      {/* Header */}
      <AppBar position="fixed" sx={{ ...glassStyle, background: 'rgba(27, 94, 32, 0.95)' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <img src="/logo.svg" alt="Green Mountain Landscaping Logo" style={{ width: 40, height: 40, marginRight: 8 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
              Green Mountain Landscaping
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button color="inherit" href="#contact" sx={{ fontWeight: 600 }}>Get Quote</Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("/images/348s.webp"), url("/images/348s.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: { xs: 'scroll', md: 'fixed' },
            zIndex: -2
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(27, 94, 32, 0.85) 0%, rgba(46, 125, 50, 0.75) 50%, rgba(76, 175, 80, 0.65) 100%)',
            zIndex: -1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{ 
                ...glassStyle, 
                p: { xs: 2, sm: 3, md: 5 }, 
                color: 'white',
                textAlign: { xs: 'center', md: 'left' }
              }}>
                <Typography 
                  variant="h1" 
                  sx={{ 
                    mb: { xs: 2, md: 3 }, 
                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' }
                  }}
                >
                  Green Mountain Landscaping
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    mb: 2, 
                    opacity: 0.95, 
                    fontWeight: 500,
                    fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }
                  }}
                >
                  Transform Your Outdoor Dreams Into Reality
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2, 
                    opacity: 0.9, 
                    fontWeight: 400,
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
                  }}
                >
                  Professional Landscape Design & Maintenance
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 3, 
                    opacity: 0.9, 
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                    lineHeight: 1.6
                  }}
                >
                  Serving North Vancouver & Metro Vancouver with premium landscaping services. Licensed, insured, and committed to excellence.
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  mb: 3, 
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <Chip 
                    icon={<CheckCircle sx={{ fontSize: { xs: 16, sm: 20 } }} />} 
                    label="Licensed & Insured" 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }} 
                  />
                  <Chip 
                    icon={<Star sx={{ fontSize: { xs: 16, sm: 20 } }} />} 
                    label="5-Star Reviews" 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }} 
                  />
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 2, sm: 3 }, 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center'
                }}>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth={true}
                    sx={{ 
                      bgcolor: 'white', 
                      color: 'primary.main', 
                      '&:hover': { bgcolor: 'grey.100', transform: 'translateY(-2px)' },
                      transition: 'all 0.3s ease',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                      py: { xs: 1.2, sm: 1.5 },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      maxWidth: { sm: '280px' }
                    }}
                    href="#contact"
                  >
                    Get Free Quote Today
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth={true}
                    sx={{ 
                      borderColor: 'white', 
                      color: 'white', 
                      '&:hover': { 
                        borderColor: 'white', 
                        bgcolor: 'rgba(255,255,255,0.1)',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease',
                      py: { xs: 1.2, sm: 1.5 },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      maxWidth: { sm: '280px' }
                    }}
                    href="tel:+16042020053"
                    startIcon={<Phone />}
                  >
                    (604) 202-0053
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ display: { xs: 'block', md: 'block' }, mt: { xs: 3, md: 0 } }}>
                <Box sx={{ 
                  ...glassStyle, 
                  p: { xs: 1, md: 2 }, 
                  borderRadius: '24px', 
                  position: 'relative' 
                }}>
                  <Box sx={{ 
                    position: 'relative', 
                    overflow: 'hidden', 
                    borderRadius: '20px', 
                    height: { xs: '250px', sm: '300px', md: '400px' }
                  }}>
                    {portfolioImages.map((image, index) => (
                      <Slide
                        key={index}
                        direction="left"
                        in={index === currentSlide}
                        mountOnEnter
                        unmountOnExit
                        timeout={1000}
                      >
                        <img
                          src={image.src}
                          alt={image.alt}
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '20px'
                          }}
                        />
                      </Slide>
                    ))}
                  </Box>
                  <IconButton
                    onClick={prevSlide}
                    sx={{
                      position: 'absolute',
                      left: { xs: 8, md: 16 },
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.8)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                      width: { xs: 36, md: 48 },
                      height: { xs: 36, md: 48 }
                    }}
                  >
                    <ArrowBackIos sx={{ fontSize: { xs: 16, md: 20 } }} />
                  </IconButton>
                  <IconButton
                    onClick={nextSlide}
                    sx={{
                      position: 'absolute',
                      right: { xs: 8, md: 16 },
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.8)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                      width: { xs: 36, md: 48 },
                      height: { xs: 36, md: 48 }
                    }}
                  >
                    <ArrowForwardIos sx={{ fontSize: { xs: 16, md: 20 } }} />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services */}
      <section id="services" aria-label="Landscaping Services">
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" component="h2" sx={{ mb: 3, color: 'primary.main' }}>
            Complete Landscaping Services in West Vancouver & Metro Vancouver BC
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
            From design to maintenance, we handle every aspect of your outdoor space with expertise and care
          </Typography>
        </Box>
        
        <Grid container spacing={4}>
          {services.map((service, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card 
                sx={{ 
                  ...glassStyle, 
                  height: '100%', 
                  p: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 16px 40px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
                      {React.cloneElement(service.icon, { sx: { fontSize: 28 } })}
                    </Avatar>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {service.title}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    {service.desc}
                  </Typography>
                  
                  <List dense>
                    {service.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={feature} sx={{ '& .MuiTypography-root': { fontSize: '0.9rem' } }} />
                      </ListItem>
                    ))}
                  </List>
                  {service.link && (
                    <Button 
                      component={RouterLink} 
                      to={service.link} 
                      variant="outlined" 
                      size="small" 
                      sx={{ mt: 2 }}
                    >
                      Learn More
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button 
            variant="contained" 
            size="large" 
            href="#contact"
            sx={{ px: 4, py: 1.5 }}
          >
            Get Free Quote Today
          </Button>
          <Button 
            variant="outlined" 
            size="large" 
            component={RouterLink}
            to="/get-quote"
            sx={{ px: 4, py: 1.5, ml: 2 }}
          >
            Detailed Quote Form
          </Button>
        </Box>
      </Container>
      </section>

      {/* Trust Indicators */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" justifyContent="center">
            <Grid item xs={12} sm={3} textAlign="center">
              <AnimatedCounter end={10} suffix="+" />
              <Typography variant="h6">Years Experience</Typography>
            </Grid>
            <Grid item xs={12} sm={3} textAlign="center">
              <AnimatedCounter end={1000} suffix="+" />
              <Typography variant="h6">Projects Completed</Typography>
            </Grid>
            <Grid item xs={12} sm={3} textAlign="center">
              <AnimatedCounter end={100} suffix="%" />
              <Typography variant="h6">Licensed & Insured</Typography>
            </Grid>
            <Grid item xs={12} sm={3} textAlign="center">
              <Rating value={5} readOnly sx={{ color: '#ffd700', mb: 1, fontSize: '2rem' }} />
              <Typography variant="h6">5-Star Reviews</Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Gallery */}
      <section id="gallery" aria-label="Work Portfolio Gallery">
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h2" component="h2" align="center" sx={{ mb: 8, color: 'primary.main' }}>
          Professional Landscaping Portfolio - West Vancouver BC
        </Typography>
        <Box sx={{ overflow: 'hidden', mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              animation: 'slideRight 20s linear infinite',
              '@keyframes slideRight': {
                '0%': { transform: 'translateX(0)' },
                '100%': { transform: 'translateX(-50%)' }
              }
            }}
          >
            {[...portfolioImages, ...portfolioImages].slice(0, Math.ceil(portfolioImages.length / 2) * 2).map((image, index) => (
              <Card
                key={index}
                sx={{
                  ...glassStyle,
                  p: 1,
                  cursor: 'pointer',
                  minWidth: '250px',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'scale(1.05)' }
                }}
                onClick={() => setSelectedImage(index % portfolioImages.length)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '12px'
                  }}
                />
              </Card>
            ))}
          </Box>
        </Box>
        <Box sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              animation: 'slideLeft 25s linear infinite',
              '@keyframes slideLeft': {
                '0%': { transform: 'translateX(-50%)' },
                '100%': { transform: 'translateX(0)' }
              }
            }}
          >
            {[...portfolioImages, ...portfolioImages].slice(Math.ceil(portfolioImages.length / 2)).map((image, index) => (
              <Card
                key={index}
                sx={{
                  ...glassStyle,
                  p: 1,
                  cursor: 'pointer',
                  minWidth: '250px',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'scale(1.05)' }
                }}
                onClick={() => setSelectedImage((index + Math.ceil(portfolioImages.length / 2)) % portfolioImages.length)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  style={{
                    width: '100%',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '12px'
                  }}
                />
              </Card>
            ))}
          </Box>
        </Box>
      </Container>
      </section>

      {/* Image Modal */}
      {selectedImage !== null && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'pointer'
          }}
          onClick={() => setSelectedImage(null)}
        >
          <Box sx={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
            <img
              src={portfolioImages[selectedImage].src}
              alt={portfolioImages[selectedImage].alt}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
            />
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(null);
              }}
              sx={{
                position: 'absolute',
                top: -40,
                right: -40,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
              }}
            >
              ✕
            </IconButton>
          </Box>
        </Box>
      )}

      {/* About */}
      <section id="about" aria-label="About Green Mountain Landscaping">
      <Box sx={{ bgcolor: 'background.default', py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" component="h2" sx={{ mb: 3, color: 'primary.main' }}>
                About Green Mountain Landscaping - Premier West Vancouver Landscapers
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Established in North Vancouver, BC, Green Mountain Landscaping is your trusted partner for comprehensive outdoor solutions. We specialize in transforming residential and commercial properties with custom landscape designs, professional maintenance, and quality hardscaping.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Our experienced team combines creativity with technical expertise to deliver landscapes that not only look stunning but also thrive in the Pacific Northwest climate. From concept to completion, we're committed to exceeding your expectations.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationOn color="primary" />
                <Typography>1504-1552 Esquimalt Ave, West Vancouver, BC V7V 1R3</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Schedule color="primary" />
                <Typography>Mon-Fri 8AM-6PM, Sat 9AM-5PM</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CheckCircle color="primary" />
                <Typography>Licensed & Insured</Typography>
              </Box>
              
              <Typography variant="h6" sx={{ mb: 3 }}>Trusted & Verified On:</Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                mb: 3,
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between'
              }}>
                <Card 
                  component={Link}
                  href="https://g.co/kgs/FhQzTu8"
                  target="_blank"
                  sx={{ 
                    p: 2, 
                    textAlign: 'center', 
                    textDecoration: 'none',
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' },
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  <Box sx={{ fontSize: '2rem', mb: 1 }}>🔍</Box>
                  <Typography variant="h6" color="primary" sx={{ fontSize: '1rem' }}>Google</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Business Listing</Typography>
                  <Rating value={5} readOnly size="small" sx={{ mt: 1 }} />
                </Card>
                <Card 
                  component={Link}
                  href="https://www.yelp.ca/biz/green-mountain-landscaping-north-vancouver-3"
                  target="_blank"
                  sx={{ 
                    p: 2, 
                    textAlign: 'center', 
                    textDecoration: 'none',
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' },
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  <Box sx={{ fontSize: '2rem', mb: 1, color: '#d32323' }}>📍</Box>
                  <Typography variant="h6" sx={{ color: '#d32323', fontSize: '1rem' }}>Yelp</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Customer Reviews</Typography>
                  <Rating value={5} readOnly size="small" sx={{ mt: 1 }} />
                </Card>
                <Card 
                  component={Link}
                  href="https://www.yellowpages.ca/bus/British-Columbia/North-Vancouver/Green-Mountain-Landscaping/101754430.html"
                  target="_blank"
                  sx={{ 
                    p: 2, 
                    textAlign: 'center', 
                    textDecoration: 'none',
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' },
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  <Box sx={{ fontSize: '2rem', mb: 1, color: '#ffcc00' }}>📞</Box>
                  <Typography variant="h6" sx={{ color: '#ffcc00', fontSize: '1rem' }}>Yellow Pages</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Business Directory</Typography>
                  <Rating value={5} readOnly size="small" sx={{ mt: 1 }} />
                </Card>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {portfolioImages.slice(0, 6).map((image, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box 
                      sx={{ 
                        ...glassStyle, 
                        p: 1, 
                        borderRadius: '16px', 
                        mb: 2,
                        transition: 'transform 0.3s ease',
                        '&:hover': { transform: 'scale(1.05)' }
                      }}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        loading="lazy"
                        style={{ 
                          width: '100%', 
                          borderRadius: '12px', 
                          height: '180px', 
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>
      </section>

      {/* Testimonials */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h2" align="center" sx={{ mb: 8, color: 'primary.main' }}>
          What Our Customers Say
        </Typography>
        <Grid container spacing={4}>
          {testimonials.map((testimonial, i) => (
            <Grid item xs={12} md={6} lg={3} key={i}>
              <Card sx={{ ...glassStyle, p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    {testimonial.name.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{testimonial.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{testimonial.location}</Typography>
                  </Box>
                </Box>
                <Rating value={testimonial.rating} readOnly sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                  "{testimonial.text}"
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ Section */}
      <section id="faq" aria-label="Frequently Asked Questions">
      <Box sx={{ bgcolor: 'background.default', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h2" component="h2" align="center" sx={{ mb: 6, color: 'primary.main' }}>
            Landscaping FAQ - West Vancouver & Metro Vancouver BC
          </Typography>
          {faqs.map((faq, i) => (
            <Accordion key={i} sx={{ mb: 2, ...glassStyle }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>
      </section>

      {/* Google Map Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" sx={{ mb: 6, color: 'primary.main' }}>
            Find Us & Service Areas
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Card sx={{ ...glassStyle, p: 2, height: '400px' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2602.8!2d-123.1572225!3d49.3301081!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x54866e17341d1bcd%3A0xebf0dfd335275185!2sGreen%20Mountain%20Landscaping!5e0!3m2!1sen!2sca!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0, borderRadius: '12px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Green Mountain Landscaping - West Vancouver"
                />
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ ...glassStyle, p: 3, height: '400px' }}>
                <Typography variant="h5" sx={{ mb: 3, color: 'primary.main' }}>
                  <Map sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Service Areas
                </Typography>
                <List>
                  {[
                    'North Vancouver',
                    'West Vancouver', 
                    'Burnaby',
                    'Richmond',
                    'Vancouver',
                    'Coquitlam',
                    'Port Moody',
                    'New Westminster'
                  ].map((area, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                      </ListItemIcon>
                      <ListItemText primary={area} />
                    </ListItem>
                  ))}
                </List>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.main', borderRadius: 2, color: 'white' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    📍 Based in North Vancouver
                  </Typography>
                  <Typography variant="body2">
                    Serving Metro Vancouver & surrounding areas
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact Form */}
      <section id="contact" aria-label="Contact Form">
      <Box sx={{ bgcolor: 'primary.main', py: 10 }}>
        <Container maxWidth="md">
          <Typography variant="h2" component="h2" align="center" sx={{ mb: 2, color: 'white' }}>
            Get Free Landscaping Quote - West Vancouver & Metro Vancouver BC
          </Typography>
          <Typography variant="h6" align="center" sx={{ mb: 6, color: 'rgba(255,255,255,0.9)' }}>
            Get your free, no-obligation quote today!
          </Typography>
          
          <Card sx={{ ...glassStyle, p: { xs: 3, md: 5 }, bgcolor: 'rgba(255,255,255,0.95)' }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    required
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    required
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label=""
                    name="service"
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    SelectProps={{ native: true }}
                  >
                    <option value="" disabled>Select a Service</option>
                    <option value="landscape-design">Landscape Design</option>
                    <option value="lawn-care">Lawn Care & Maintenance</option>
                    <option value="garden-design">Garden Design & Planting</option>
                    <option value="hardscaping">Hardscaping & Patios</option>
                    <option value="irrigation">Irrigation Systems</option>
                    <option value="maintenance">Landscape Maintenance</option>
                    <option value="other">Other</option>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Project Details"
                    placeholder="Tell us about your project, property size, timeline, and any specific requirements..."
                    multiline
                    rows={4}
                    required
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={state.submitting}
                    startIcon={state.submitting ? <Skeleton width={20} height={20} /> : <Send />}
                    sx={{ py: 2, fontSize: '1.1rem' }}
                  >
                    {state.submitting ? 'Sending Your Request...' : 'Get My Free Quote'}
                  </Button>
                </Grid>
              </Grid>
            </form>
            
            <Divider sx={{ my: 4 }} />
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Or Contact Us Directly</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<Phone />}
                  href="tel:+16042020053"
                  sx={{ borderRadius: 8 }}
                >
                  (604) 202-0053
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Email />}
                  href="mailto:lolooit@gmail.com"
                  sx={{ borderRadius: 8 }}
                >
                  Email Us
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<WhatsApp />}
                  href="https://wa.me/16042020053"
                  target="_blank"
                  sx={{ borderRadius: 8, color: '#25d366', borderColor: '#25d366' }}
                >
                  WhatsApp
                </Button>
              </Box>
            </Box>
          </Card>
        </Container>
      </Box>
      </section>

      {/* Footer */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
        color: 'white', 
        py: 8 
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <img src="/logo.svg" alt="Logo" style={{ width: 40, height: 40, marginRight: 12 }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Green Mountain
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ opacity: 0.9, mb: 2, lineHeight: 1.6 }}>
                Professional landscape design & maintenance services in West Vancouver, BC
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  icon={<CheckCircle sx={{ fontSize: 16 }} />} 
                  label="Licensed & Insured" 
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
                <Chip 
                  label="Free Quotes" 
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Contact Info</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                  <Link href="tel:+16042020053" color="inherit" underline="hover">
                    (604) 202-0053
                  </Link>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                  <Link href="mailto:lolooit@gmail.com" color="inherit" underline="hover">
                    lolooit@gmail.com
                  </Link>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LocationOn sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)', mt: 0.2 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    1504-1552 Esquimalt Ave<br />West Vancouver, BC V7V 1R3
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Mon-Fri 8AM-6PM, Sat 9AM-5PM
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Connect With Us</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <IconButton 
                  href="tel:+16042020053" 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)', transform: 'translateY(-2px)' },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Phone />
                </IconButton>
                <IconButton 
                  href="mailto:lolooit@gmail.com" 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)', transform: 'translateY(-2px)' },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Email />
                </IconButton>
                <IconButton 
                  href="https://wa.me/16042020053" 
                  target="_blank"
                  sx={{ 
                    bgcolor: '#25d366', 
                    color: 'white',
                    '&:hover': { bgcolor: '#128c7e', transform: 'translateY(-2px)' },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <WhatsApp />
                </IconButton>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                Service Areas:
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.5 }}>
                West Vancouver • North Vancouver • Burnaby • Richmond • Vancouver & surrounding Metro Vancouver areas
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)', my: 4 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              © 2024 Green Mountain Landscaping. All rights reserved. | Professional Landscaping Services in Metro Vancouver
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Scroll Buttons */}
      {showScrollButtons && (
        <>
          <Fab
            onClick={scrollToTop}
            sx={{
              position: 'fixed',
              bottom: 140,
              right: 16,
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            <KeyboardArrowUp />
          </Fab>
          <Fab
            onClick={scrollToBottom}
            sx={{
              position: 'fixed',
              bottom: 80,
              right: 16,
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            <KeyboardArrowDown />
          </Fab>
        </>
      )}

      {/* WhatsApp FAB */}
      <Fab
        color="success"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          bgcolor: '#25d366',
          '&:hover': { bgcolor: '#128c7e' }
        }}
        href="https://wa.me/16042020053"
        target="_blank"
      >
        <WhatsApp />
      </Fab>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/lawn-care-vancouver" element={<LawnCareVancouver />} />
            <Route path="/irrigation-systems" element={<IrrigationSystems />} />
            <Route path="/garden-design" element={<GardenDesign />} />
            <Route path="/hardscaping" element={<Hardscaping />} />
            <Route path="/landscape-maintenance" element={<LandscapeMaintenance />} />
            <Route path="/get-quote" element={<GetQuote />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;